import {
  Button,
  Modal,
  type ModalProps,
} from '@omnipresentgroup/design-system';
import { Form } from '@omnipresentgroup/flexible-forms';
import { ExpenseStatusEnum } from '@omnipresentgroup/types';
import type { IChangeEvent } from '@rjsf/core';
import { useState } from 'react';

export type ExpenseStatusUpdate =
  | ExpenseStatusEnum.INFO_NEEDED
  | ExpenseStatusEnum.REJECTED;

type FormFields = {
  reason: string;
};

export const ExpenseStatusUpdateModal = ({
  modalProps,
  updateMode,
  handleSubmit,
  handleCancel,
  isLoading,
}: {
  modalProps: Omit<ModalProps, 'children'>;
  updateMode: ExpenseStatusUpdate;
  handleSubmit: (reason: string) => void;
  handleCancel: () => void;
  isLoading?: boolean;
}) => {
  const [formValues, setFormValues] = useState<FormFields>();

  const formProps = {
    [ExpenseStatusEnum.INFO_NEEDED]: {
      header: 'Request changes',
      label: 'Requested changes',
      placeholder: 'Specify the requested changes',
    },
    [ExpenseStatusEnum.REJECTED]: {
      header: 'Reject expense',
      label: 'Reason for rejection',
      placeholder: 'Specify the reasons for rejection',
    },
  };

  const FORM_ID = `${updateMode}-form`;

  // Form Change
  const onChange = ({ formData }: IChangeEvent<FormFields>) => {
    setFormValues(formData);
  };

  // Form Submit
  const onSubmit = () => {
    if (formValues) {
      handleSubmit(formValues.reason);
    }
  };

  return (
    <Modal span={5} {...modalProps}>
      <Modal.Header
        title={formProps?.[updateMode!]?.header}
        onClose={handleCancel}
      />
      <Modal.Body>
        <Form<FormFields>
          id={FORM_ID}
          schema={{
            type: 'object',
            required: ['reason'],
            properties: {
              reason: {
                type: 'string',
                title: formProps?.[updateMode!]?.label,
              },
            },
          }}
          uiSchema={{
            'ui:submitButtonOptions': {
              norender: true,
            },
            reason: {
              'ui:widget': 'textarea',
              'ui:placeholder': formProps?.[updateMode!]?.placeholder,
            },
          }}
          formData={formValues}
          onChange={onChange}
          onSubmit={onSubmit}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          form={FORM_ID}
          loading={isLoading}
          disabled={!formValues?.reason}
        >
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
